// The welcome screen + its ambient ghosts — 05i-2. main.js's DOMContentLoaded welcome setup,
// flexBlock / spawnGhost / attemptGhostMerge / mergeGhosts and dismissWelcomeScreen, copied
// exactly (05i-2 fork 2A: plain elements, as main.js made them — random timers + CSS class
// changes, so a straight copy keeps every beat). The sums are welcome-rules.ts.
//
// The welcome card itself is legacy-scaffold.html's (inside #workspace, byte-identical); the
// ghosts are plain <div>s appended to #workspace next to it. React's modules share that
// element through a portal and never touch these.

import { pixelSize } from "./module-shell.js";
import { STEP } from "./placement.js";
import { getGridCols, getGridRows } from "./canvas-space.js";
import {
  FIRST_REPLACEMENT_ID,
  GHOST_FLEX_INTERVAL_MS,
  GHOST_MERGE_INTERVAL_MS,
  INITIAL_GHOSTS,
  NO_WELCOME,
  flexStep,
  grownSize,
  initialGhostCell,
  isAutoAreaClear,
  mirroredTarget,
  pickMergePair,
  spawnCell,
  visibleGhostBounds,
  welcomeRectFor,
  type Cell,
  type Ghost,
} from "./welcome-rules.js";

interface LiveGhost extends Ghost {
  el: HTMLElement;
}

let ghosts: LiveGhost[] = [];
let welcome: Cell = NO_WELCOME;
// Non-null while the welcome screen is up: a merge's timeouts check it, so a dismiss
// mid-merge stops the merge from spawning a replacement.
let flexTimer: number | null = null;
let mergeTimer: number | null = null;
let nextGhostId = FIRST_REPLACEMENT_ID;
// One merging pair on screen at a time: a merge's own ~1.5s animation can still be running
// when the next 3s tick fires.
let mergeInFlight = false;

function byId(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} missing from legacy-scaffold.html`);
  return el;
}

/** The grid cells the welcome card covers (main.js's window.welcomeRect): a phone tile tap's
 *  first empty slot keeps clear of it. */
export function welcomeRect(): Cell {
  return welcome;
}

const bounds = () => visibleGhostBounds(window.innerWidth, window.innerHeight, STEP);

function isClear(cell: Cell, ignoreId: number): boolean {
  return isAutoAreaClear(cell, ignoreId, { gridCols: getGridCols(), gridRows: getGridRows(), welcome, ghosts });
}

function place(el: HTMLElement, c: Cell): void {
  el.style.left = c.col * STEP + "px";
  el.style.top = c.row * STEP + "px";
  el.style.width = pixelSize(c.cols) + "px";
  el.style.height = pixelSize(c.rows) + "px";
}

function flexBlock(g: LiveGhost): void {
  const next = flexStep(g, Math.random, (c) => isClear(c, g.id));
  if (!next) return;
  Object.assign(g, next);
  place(g.el, next);
}

function spawnGhost(targetCol: number, targetRow: number): void {
  const cell = spawnCell(targetCol, targetRow, Math.random, bounds(), (c) => isClear(c, -1));
  if (!cell) return;
  const el = document.createElement("div");
  el.className = "autonomous-ghost ghost-merging";
  place(el, cell);
  byId("workspace").appendChild(el);
  ghosts.push({ id: nextGhostId++, el, ...cell });
  // Born blue (a merge's own replacement), then relaxes to green — two frames, so the blue
  // actually paints before the class comes off.
  requestAnimationFrame(() => requestAnimationFrame(() => el.classList.remove("ghost-merging")));
}

function attemptGhostMerge(): void {
  if (mergeInFlight) return;
  const pair = pickMergePair(ghosts, Math.random);
  if (pair) mergeGhosts(pair[0], pair[1]);
}

/**
 * Two ghosts drift together and merge into a bigger one: both turn blue (the same electric
 * blue as a real module merge), `a` slides onto `b` (the ghosts' own 0.7s transition) and
 * takes b's size, then the module merge's shake → burst / absorb keyframes (styles.css —
 * generic, no module content). The replacement spawns mirrored across the screen.
 */
function mergeGhosts(a: LiveGhost, b: LiveGhost): void {
  mergeInFlight = true;
  a.merging = true;
  b.merging = true;
  a.el.classList.add("ghost-merging");
  b.el.classList.add("ghost-merging");
  Object.assign(a, { col: b.col, row: b.row, cols: b.cols, rows: b.rows });
  place(a.el, a);

  setTimeout(() => {
    if (flexTimer === null) {
      mergeInFlight = false; // dismissed mid-merge
      return;
    }
    a.el.classList.add("merge-shaking");
    b.el.classList.add("merge-shaking");

    setTimeout(() => {
      a.el.classList.remove("merge-shaking");
      b.el.classList.remove("merge-shaking");
      if (flexTimer === null) {
        mergeInFlight = false;
        return;
      }
      // `a` leaves the grid now, before b's growth is checked, so that check (and every
      // flexBlock from here on) sees the merge done; its element stays for the absorb.
      const idx = ghosts.indexOf(a);
      if (idx !== -1) ghosts.splice(idx, 1);

      const grown = grownSize(a, b);
      if (isClear({ col: b.col, row: b.row, ...grown }, b.id)) {
        Object.assign(b, grown);
        b.el.style.width = pixelSize(b.cols) + "px";
        b.el.style.height = pixelSize(b.rows) + "px";
      }
      b.el.classList.add("merge-bursting");
      a.el.classList.add("merge-absorbing");

      setTimeout(() => {
        b.el.classList.remove("merge-bursting");
        b.el.classList.remove("ghost-merging");
        b.merging = false;
        a.el.remove();
        mergeInFlight = false;
        if (flexTimer === null) return;
        const target = mirroredTarget(b, bounds());
        spawnGhost(target.col, target.row);
      }, 420);
    }, 350);
  }, 700);
}

/** Fade the welcome card and the ghosts out (any first module, the side panel opening, a
 *  preset, Undo…). Does nothing once dismissed. */
export function dismissWelcomeScreen(): void {
  if (flexTimer === null) return;
  clearInterval(flexTimer);
  flexTimer = null;
  if (mergeTimer !== null) {
    clearInterval(mergeTimer);
    mergeTimer = null;
  }
  ghosts.forEach((g) => {
    g.el.style.opacity = "0";
    setTimeout(() => g.el.remove(), 500);
  });
  ghosts = [];
  const ws = document.getElementById("welcome-screen");
  if (ws) {
    ws.style.opacity = "0";
    setTimeout(() => ws.remove(), 500);
  }
}

/** The two A's in "VARIANCE" swap places every 5s. Re-measured each tick, so a font or
 *  layout shift can't skew the swap. */
function animateVarianceAs(id1: string, id2: string): void {
  const a1 = document.getElementById(id1);
  const a2 = document.getElementById(id2);
  if (!a1 || !a2) return;
  let dist = 0;
  let swapped = false;
  const measure = () => {
    const d = a2.getBoundingClientRect().left - a1.getBoundingClientRect().left;
    if (d > 0) dist = d;
  };
  // Wait for the web fonts, so Barlow Condensed's metrics are in before the first measure.
  void document.fonts.ready.then(measure);
  setInterval(() => {
    measure();
    if (!dist) return;
    swapped = !swapped;
    for (const a of [a1, a2]) {
      a.style.transition = "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)";
      a.style.animation = "variance-a-fade 0.7s ease-in-out";
      a.addEventListener("animationend", () => (a.style.animation = ""), { once: true });
    }
    a1.style.transform = swapped ? "translateX(" + dist + "px)" : "";
    a2.style.transform = swapped ? "translateX(" + -dist + "px)" : "";
  }, 5000);
}

/** main.js's DOMContentLoaded welcome setup. */
function initWelcome(): void {
  const ws = document.getElementById("welcome-screen");
  if (!ws) return;

  const centerX = window.innerWidth / 2 - 56;
  const centerY = window.innerHeight / 2 - 16;
  ws.style.left = centerX + "px";
  ws.style.top = centerY + "px";
  // `|| 560 / 340`: main.js's size before layout, kept.
  welcome = welcomeRectFor(centerX, centerY, ws.offsetWidth || 560, ws.offsetHeight || 340, STEP);

  const b = bounds();
  const workspace = byId("workspace");
  for (let i = 0; i < INITIAL_GHOSTS; i++) {
    const cell = initialGhostCell(Math.random, b, (c) => isClear(c, -1));
    if (!cell) continue;
    const el = document.createElement("div");
    el.className = "autonomous-ghost";
    place(el, cell);
    workspace.appendChild(el);
    ghosts.push({ id: i, el, ...cell });
  }

  flexTimer = window.setInterval(() => ghosts.forEach(flexBlock), GHOST_FLEX_INTERVAL_MS);
  mergeTimer = window.setInterval(attemptGhostMerge, GHOST_MERGE_INTERVAL_MS);

  // The tour is disabled for now (tour.js), and both buttons are hidden — kept wired.
  const startTour = () => {
    if (window.startTour) window.startTour();
  };
  byId("welcome-tour-btn").addEventListener("click", startTour);
  document.getElementById("desktop-tour-btn")?.addEventListener("click", startTour);
  byId("welcome-dismiss-btn").addEventListener("click", () => dismissWelcomeScreen());

  animateVarianceAs("var-a1", "var-a2");
  // The phone welcome's copy of the word (mobile, 05k) — started here, as main.js did.
  animateVarianceAs("var-a1-m", "var-a2-m");
}

/** Installed before main.js loads; runs on the same (synthetic) DOMContentLoaded main.js's
 *  setup ran on — App.tsx fires it once every script is in. */
export function installWelcome(): void {
  window.addEventListener("DOMContentLoaded", initWelcome);
}
